import type { GameStateResponse, PublicPlayer, PublicRound } from "@kalaporssi/shared";
import QRCode from "qrcode";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router";
import { Board } from "../components/Board";
import { CatchCard } from "../components/CatchCard";
import { ForecastTable } from "../components/ForecastTable";
import { ErrorBox, Loading } from "../components/Misc";
import { MarketTable, MonthResults, Standings, WhoChose } from "../components/Results";
import { api } from "../lib/api";
import { monthName } from "../lib/format";
import { useElapsed, useGame, useResult } from "../lib/game";
import { Final } from "./Player";

/** Host console, designed for a projector (SPEC §12.2). */
export function HostConsole() {
  const code = (useParams().code ?? "").toUpperCase();
  const { state, connection, error, accept } = useGame(code);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<unknown>(null);

  const run = async (fn: () => Promise<GameStateResponse>) => {
    setBusy(true);
    setActionError(null);
    try {
      accept(await fn());
    } catch (e) {
      setActionError(e);
    } finally {
      setBusy(false);
    }
  };
  const startAndOpen = () =>
    run(async () => {
      if (state?.status === "LOBBY") accept(await api().startGame(code));
      return api().openRound(code);
    });
  const openNext = () => run(() => api().openRound(code));
  const remove = (p: PublicPlayer) => {
    if (window.confirm(`Poistetaanko ${p.nickname} pelistä? Hän ei voi enää valita.`)) {
      void run(() => api().removePlayer(code, p.id));
    }
  };
  const retry = (n: number) => run(() => api().retryResolve(code, n));

  const round = state?.currentRound ?? null;
  const month = state?.status === "FINISHED" ? 12 : round ? round.month : -1;
  const common = { state, busy, remove };

  return (
    <>
      <Board month={month} players={state?.status === "LOBBY" ? undefined : state?.players} connection={connection} />
      <main className="stack">
        <ErrorBox error={error} />
        <ErrorBox error={actionError} />
        {!state && !error && <Loading />}
        {state?.status === "LOBBY" && <Lobby {...common} state={state} onStart={startAndOpen} />}
        {state?.status === "STARTED" && (
          <div className="cover">
            <div className="big">Peli alkoi</div>
            <button type="button" className="btn tag" onClick={openNext} disabled={busy}>
              Avaa tammikuu
            </button>
          </div>
        )}
        {state?.status === "ROUND_OPEN" && round && (
          <OpenRound state={state} round={round} busy={busy} remove={remove} retry={retry} />
        )}
        {state?.status === "ROUND_RESOLVED" && round && (
          <Resolved state={state} round={round} busy={busy} onNext={openNext} />
        )}
        {state?.status === "FINISHED" && <Final state={state} />}
      </main>
    </>
  );
}

function Qr({ url }: { url: string }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    if (ref.current) void QRCode.toCanvas(ref.current, url, { width: 360, margin: 1, errorCorrectionLevel: "M" });
  }, [url]);
  return (
    <div className="qr">
      <canvas ref={ref} role="img" aria-label={`QR-koodi osoitteeseen ${url}`} />
    </div>
  );
}

function PlayerChips(props: { players: PublicPlayer[]; answeredIds?: string[]; remove?: (p: PublicPlayer) => void }) {
  const answered = new Set(props.answeredIds ?? []);
  const showState = props.answeredIds !== undefined;
  return (
    <div className="people">
      {props.players
        .filter((p) => p.active && p.type === "human")
        .map((p) => {
          const done = answered.has(p.id);
          return (
            <span key={p.id} className={`st${done ? " done" : ""}`}>
              {showState && <span className="dot" aria-hidden="true" />}
              {p.nickname}
              {showState && <span className="sr-only">{done ? "vastannut" : "odottaa"}</span>}
              {props.remove && !done && (
                <button
                  type="button"
                  className="btn ghost small"
                  onClick={() => props.remove?.(p)}
                  aria-label={`Poista ${p.nickname}`}
                  style={{ padding: "0 6px" }}
                >
                  ×
                </button>
              )}
            </span>
          );
        })}
    </div>
  );
}

function Lobby(props: {
  state: GameStateResponse;
  busy: boolean;
  onStart: () => void;
  remove: (p: PublicPlayer) => void;
}) {
  const { state } = props;
  const url = `${window.location.origin}/j/${state.code}`;
  const humans = state.players.filter((p) => p.type === "human" && p.active);
  const ais = state.players.filter((p) => p.type === "ai");
  return (
    <div className="lobby">
      <Qr url={url} />
      <div className="stack">
        <span className="muted">Liity osoitteessa {window.location.host} koodilla</span>
        <div className="code">{state.code}</div>
        <h3>Pelaajat ({humans.length})</h3>
        {humans.length === 0 ? (
          <p className="muted">Odotetaan pelaajia…</p>
        ) : (
          <PlayerChips players={state.players} remove={props.remove} />
        )}
        {ais.length > 0 && (
          <div className="ai">
            {ais.map((a) => (
              <span key={a.id} className="st">
                {a.nickname} <span className="muted small">tekoäly</span>
              </span>
            ))}
          </div>
        )}
        <div>
          <button
            type="button"
            className="btn tag"
            onClick={props.onStart}
            disabled={props.busy || humans.length === 0}
          >
            Aloita peli ja tammikuu
          </button>
        </div>
      </div>
    </div>
  );
}

function OpenRound(props: {
  state: GameStateResponse;
  round: PublicRound;
  busy: boolean;
  remove: (p: PublicPlayer) => void;
  retry: (n: number) => void;
}) {
  const { state, round } = props;
  const elapsed = useElapsed(round.openedAt);
  const answered = new Set(round.answeredIds);
  const ais = state.players.filter((p) => p.type === "ai" && p.active);
  const mm = `${Math.floor(elapsed / 60)}:${String(elapsed % 60).padStart(2, "0")}`;
  return (
    <div className="stack">
      <div className="monthhead">
        <h2>{monthName(round.month)}</h2>
        <span className="muted">
          Kierros {round.roundNo}/12 · {state.players.filter((p) => p.active).length} pelaajaa · auki {mm}
        </span>
      </div>
      <div className="panel stack">
        <div className="row spread">
          <b>
            {round.answered}/{round.expected} vastannut
          </b>
          {round.status === "RESOLVING" && <span className="muted">Pörssi laskee tuloksia…</span>}
        </div>
        <div className="progress" aria-hidden="true">
          <div style={{ width: `${(round.answered / Math.max(1, round.expected)) * 100}%` }} />
        </div>
        <PlayerChips players={state.players} answeredIds={round.answeredIds} remove={props.remove} />
        {ais.length > 0 && (
          <div className="ai">
            {ais.map((a) => {
              const done = answered.has(a.id);
              return (
                <span key={a.id} className={`st${done ? " done" : ""}`}>
                  <span className="dot" aria-hidden="true" />
                  {a.nickname}: {done ? "valmis" : "pohtii"}
                </span>
              );
            })}
          </div>
        )}
        {round.status === "RESOLVING" && elapsed > 90 && (
          <button
            type="button"
            className="btn ghost small"
            onClick={() => props.retry(round.roundNo)}
            disabled={props.busy}
          >
            Laske tulokset uudelleen
          </button>
        )}
      </div>
      <ForecastTable round={round} />
      <h3>Kuukauden saaliit</h3>
      <div className="grid">
        {round.catches.map((c) => (
          <CatchCard key={c.index} c={c} />
        ))}
      </div>
    </div>
  );
}

function Resolved(props: { state: GameStateResponse; round: PublicRound; busy: boolean; onNext: () => void }) {
  const { state, round } = props;
  const { data: result, error } = useResult(state.code, round.roundNo);
  const next = round.roundNo < 12 ? monthName(round.roundNo).toLowerCase() : null;
  return (
    <div className="stack">
      <div className="row spread">
        <div className="monthhead">
          <h2>Pörssi: {monthName(round.month).toLowerCase()}</h2>
        </div>
        {next && (
          <button type="button" className="btn tag" onClick={props.onNext} disabled={props.busy}>
            Avaa {next}
          </button>
        )}
      </div>
      <ErrorBox error={error} />
      {!result && !error && <Loading />}
      {result && (
        <>
          <MarketTable result={result} />
          <h3>Kuukauden tulos</h3>
          <MonthResults result={result} catches={round.catches} players={state.players} />
          <h3>Kuka valitsi mitä</h3>
          <WhoChose result={result} catches={round.catches} players={state.players} />
          <h3>Kokonaistilanne</h3>
          <Standings players={state.players} />
          {next && (
            <button type="button" className="btn tag" onClick={props.onNext} disabled={props.busy}>
              Avaa {next}
            </button>
          )}
        </>
      )}
    </div>
  );
}
